import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './lib/AuthContext'
import { useDishLibrary, usePlanLibrary, useProfile, useAiPlan } from './lib/hooks'
import { registerDishes } from './data/foods'
import { registerPlanLibrary } from './data/plan'
import LoadingScreen from './components/LoadingScreen'
import Onboarding from './components/Onboarding'
import PlanNotReady from './components/PlanNotReady'
import BottomNav from './components/BottomNav'
import Sidebar from './components/Sidebar'
import TopHeader from './components/TopHeader'
import Login from './pages/Login'
import Today from './pages/Today'
import Calendar from './pages/Calendar'
import Groceries from './pages/Groceries'
import Progress from './pages/Progress'
import Settings from './pages/Settings'
import Dishes from './pages/Dishes'
import DayRoute from './pages/DayRoute'
import './Health.css'

// This subdomain shares one build with the rest of the site, so the browser
// tab title / manifest / home-screen icon default to the main portfolio's.
// Swap them here so "Add to Home Screen" on health.shashankupadhyaya.com
// gets its own name and icon instead.
function useHealthPwaIdentity() {
  useEffect(() => {
    document.title = 'HealthNest'

    const setLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`)
      if (!el) {
        el = document.createElement('link')
        el.rel = rel
        document.head.appendChild(el)
      }
      el.href = href
    }
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.name = name
        document.head.appendChild(el)
      }
      el.content = content
    }

    setLink('manifest', '/health-manifest.json')
    setLink('apple-touch-icon', '/health-icons/apple-touch-icon.png')
    setLink('icon', '/health-icons/icon-192.png')
    setMeta('theme-color', '#0e0e0e')
    setMeta('apple-mobile-web-app-capable', 'yes')
    setMeta('apple-mobile-web-app-status-bar-style', 'black-translucent')
    setMeta('apple-mobile-web-app-title', 'HealthNest')
  }, [])
}

function AppShell() {
  const { user } = useAuth()
  const location = useLocation()
  const { dishes, loading: dishesLoading } = useDishLibrary()
  const [dishesRegistered, setDishesRegistered] = useState(false)
  const { planLib, loading: planLoading } = usePlanLibrary()
  const { profile, loading: profileLoading } = useProfile()
  const { aiPlan, loading: aiPlanLoading } = useAiPlan()

  useEffect(() => {
    if (!dishesLoading && dishes) {
      registerDishes(dishes)
      setDishesRegistered(true)
    }
  }, [dishesLoading, dishes])

  // The bundled data/workouts.js, data/menus.js and data/diet.js (and this
  // Firestore-sourced override of them) are Shashank's own plan and are no
  // longer shown to anyone as a default - see the aiPlan gate below. This
  // effect is now effectively vestigial (kept non-blocking, harmless) since
  // getDayPlan()'s static fallback is unreachable for any gated route.
  useEffect(() => {
    if (!planLoading && planLib) registerPlanLibrary(planLib)
  }, [planLoading, planLib])

  if (user === undefined) return <LoadingScreen />
  if (user === null) return <Login />
  if (dishesLoading || !dishesRegistered) return <LoadingScreen message="Loading food database" />
  if (profileLoading) return <LoadingScreen />
  if (!profile?.onboardingComplete) return <Onboarding />
  if (aiPlanLoading) return <LoadingScreen />
  // No personalized plan generated for this account - never fall back to
  // showing the shared/bundled default plan (that's Shashank's own plan,
  // stored under his account only - see users/{uid}/meta/aiPlan). Settings
  // stays reachable so the person can (re)try generating theirs.
  // Settings (to fix the missing plan) and Dishes (a general recipe
  // library, not tied to any personal plan) stay reachable either way.
  if (!aiPlan && location.pathname !== '/settings' && location.pathname !== '/dishes') return <PlanNotReady />

  return (
    <>
      <Sidebar />
      <div className="app-shell">
        <TopHeader />
        <main className="app-main">
          <Outlet />
        </main>
        <BottomNav />
      </div>
    </>
  )
}

export default function Health() {
  useHealthPwaIdentity()

  return (
    <div className="health-app">
      <div className="health-frame">
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<AppShell />}>
                <Route path="/today" element={<Today />} />
                <Route path="/calendar" element={<Calendar />} />
                <Route path="/groceries" element={<Groceries />} />
                <Route path="/progress" element={<Progress />} />
                <Route path="/dishes" element={<Dishes />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/day/:dayNumber" element={<DayRoute />} />
                <Route path="*" element={<Navigate to="/today" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </div>
    </div>
  )
}
